import { Capacitor } from '@capacitor/core';
import { store, ProductType, Platform, ErrorCode, LogLevel } from 'capacitor-plugin-cdv-purchase';
import { authAPI } from '../utils/api';

const APPLE_PLATFORM = Platform.APPLE_APPSTORE;

// ⚠️ IMPORTANT: Only include product IDs that are registered in App Store Connect.
// Adding unknown IDs will cause StoreKit initialization to fail silently.
// ⚠️ All 4 patient plans are AUTO-RENEWABLE SUBSCRIPTIONS in App Store Connect.
//    They MUST be registered as ProductType.PAID_SUBSCRIPTION — NOT CONSUMABLE.
//    Using CONSUMABLE for a subscription product causes StoreKit to reject the purchase
//    with the generic "Failed to place orders" error (SKErrorPaymentNotAllowed / SKErrorInvalid).
const PRODUCT_IDS = {
  patient: {
    basic: 'com.vivara.healthcare.patient.basic.monthlyv2',
    premium: 'com.vivara.healthcare.patient.premium.monthlyv2',
    gold: 'com.vivara.healthcare.patient.gold.2monthv2',
    platinum: 'com.vivara.healthcare.patient.platinum.4monthv2',
  },
  payPerUse: {
    chat: 'com.vivara.healthcare.chat.single',
    voice: 'com.vivara.healthcare.voice.single',
    video: 'com.vivara.healthcare.video.single',
    yoga: 'com.vivara.healthcare.yoga.single',
  },
  doctor: {
    sixMonth: 'com.vivara.healthcare.doctor.6monthv2',
    yearly: 'com.vivara.healthcare.doctor.yearlyv2',
    blueTick: 'com.vivara.healthcare.doctor.bluetick.monthly',
    reels: 'com.vivara.healthcare.doctor.reels.yearly',
    featured: 'com.vivara.healthcare.doctor.featured.monthly',
  },
  retailer: {
    sixMonth: 'com.vivara.healthcare.retailer.6monthv2',
    blueTick: 'com.vivara.healthcare.retailer.bluetick.monthly',
    reels: 'com.vivara.healthcare.retailer.reels.yearly',
    featured: 'com.vivara.healthcare.retailer.featured.monthly',
  },
  clinic: {
    yearly: 'com.vivara.healthcare.clinic.yearlyv2',
    blueTick: 'com.vivara.healthcare.clinic.bluetick.monthly',
    reels: 'com.vivara.healthcare.clinic.reels.yearly',
    featured: 'com.vivara.healthcare.clinic.featured.monthly',
  },
  hospital: {
    yearly: 'com.vivara.healthcare.hospital.yearlyv2',
    blueTick: 'com.vivara.healthcare.hospital.bluetick.monthly',
    reels: 'com.vivara.healthcare.hospital.reels.monthly',
    featured: 'com.vivara.healthcare.hospital.featured.monthly',
  },
};

const allProductIDs = () => {
  const ids = [];
  for (const group of Object.values(PRODUCT_IDS)) {
    for (const id of Object.values(group)) {
      ids.push(id);
    }
  }
  return ids;
};

// Maps each product ID to its StoreKit type — MUST match App Store Connect exactly.
// BUG FIX (BUG 1): Patient plans were incorrectly set to CONSUMABLE.
// Auto-renewable subscriptions MUST be PAID_SUBSCRIPTION. Using CONSUMABLE for a
// subscription product causes StoreKit to reject purchases with "Failed to place orders".
const PRODUCT_TYPE_MAP = {
  // ✅ Auto-renewable subscriptions (patient plans)
  [PRODUCT_IDS.patient.basic]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.patient.premium]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.patient.gold]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.patient.platinum]: ProductType.PAID_SUBSCRIPTION,
  // ✅ Consumables (pay-per-use)
  [PRODUCT_IDS.payPerUse.chat]: ProductType.CONSUMABLE,
  [PRODUCT_IDS.payPerUse.voice]: ProductType.CONSUMABLE,
  [PRODUCT_IDS.payPerUse.video]: ProductType.CONSUMABLE,
  [PRODUCT_IDS.payPerUse.yoga]: ProductType.CONSUMABLE,
  // ✅ Auto-renewable subscriptions (provider plans)
  [PRODUCT_IDS.doctor.sixMonth]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.doctor.yearly]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.doctor.blueTick]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.doctor.reels]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.doctor.featured]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.retailer.sixMonth]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.retailer.blueTick]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.retailer.featured]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.clinic.yearly]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.clinic.blueTick]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.clinic.featured]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.hospital.yearly]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.hospital.blueTick]: ProductType.PAID_SUBSCRIPTION,
  [PRODUCT_IDS.hospital.featured]: ProductType.PAID_SUBSCRIPTION,
};

// ─── Fallback prices from App Store Connect ──────────────────────────────────
// These are shown immediately on iOS while we wait for Apple to return real prices.
// They match the exact prices configured in App Store Connect.
export const APP_STORE_FALLBACK_PRICES = {
  // Patient subscriptions
  'com.vivara.healthcare.patient.basic.monthlyv2': { display: '₹1,199', duration: '1 Month' },
  'com.vivara.healthcare.patient.premium.monthlyv2': { display: '₹1,999', duration: '1 Month' },
  'com.vivara.healthcare.patient.gold.2monthv2': { display: '₹3,499', duration: '2 Months' },
  'com.vivara.healthcare.patient.platinum.4monthv2': { display: '₹5,499', duration: '4 Months' },
  // Patient pay-per-use
  'com.vivara.healthcare.chat.single': { display: '₹149', duration: 'Single' },
  'com.vivara.healthcare.voice.single': { display: '₹399', duration: 'Single' },
  'com.vivara.healthcare.video.single': { display: '₹599', duration: 'Single' },
  'com.vivara.healthcare.yoga.single': { display: '₹299', duration: 'Single' },
  // Doctor plans
  'com.vivara.healthcare.doctor.6monthv2': { display: '₹6,499', duration: '6 Months' },
  'com.vivara.healthcare.doctor.yearlyv2': { display: '₹11,999', duration: '1 Year' },
  'com.vivara.healthcare.doctor.bluetick.monthly': { display: '₹1,699', duration: '1 Month' },
  'com.vivara.healthcare.doctor.reels.yearly': { display: '₹999', duration: '1 Year' },
  'com.vivara.healthcare.doctor.featured.monthly': { display: '₹2,199', duration: '1 Month' },
  // Retailer plans
  'com.vivara.healthcare.retailer.6month': { display: '₹9,499', duration: '6 Months' },
  'com.vivara.healthcare.retailer.bluetick.monthly': { display: '₹1,699', duration: '1 Month' },
  'com.vivara.healthcare.retailer.reels.yearly': { display: '₹999', duration: '1 Year' },
  'com.vivara.healthcare.retailer.featured.monthly': { display: '₹2,199', duration: '1 Month' },
  // Clinic plans
  'com.vivara.healthcare.clinic.yearly': { display: '₹39,999', duration: '1 Year' },
  'com.vivara.healthcare.clinic.bluetick.monthly': { display: '₹2,499', duration: '1 Month' },
  'com.vivara.healthcare.clinic.reels.yearly': { display: '₹999', duration: '1 Year' },
  'com.vivara.healthcare.clinic.featured.monthly': { display: '₹6,999', duration: '1 Month' },
  // Hospital plans
  'com.vivara.healthcare.hospital.yearly': { display: '₹55,999', duration: '1 Year' },
  'com.vivara.healthcare.hospital.bluetick.monthly': { display: '₹7,999', duration: '1 Month' },
  'com.vivara.healthcare.hospital.reels.monthly': { display: '₹999', duration: '1 Month' },
  'com.vivara.healthcare.hospital.featured.monthly': { display: '₹14,999', duration: '1 Month' },
};

// Duration in days used when activating the subscription on the backend
const SUBSCRIPTION_DURATION_MAP = {
  [PRODUCT_IDS.patient.basic]: 30,
  [PRODUCT_IDS.patient.premium]: 30,
  [PRODUCT_IDS.patient.gold]: 60,
  [PRODUCT_IDS.patient.platinum]: 120,  // 4 months
  [PRODUCT_IDS.doctor.sixMonth]: 180,
  [PRODUCT_IDS.doctor.yearly]: 365,
  [PRODUCT_IDS.doctor.blueTick]: 30,
  [PRODUCT_IDS.doctor.reels]: 365,
  [PRODUCT_IDS.doctor.featured]: 30,
  [PRODUCT_IDS.retailer.sixMonth]: 180,
  [PRODUCT_IDS.retailer.blueTick]: 30,
  [PRODUCT_IDS.retailer.reels]: 365,
  [PRODUCT_IDS.retailer.featured]: 30,
  [PRODUCT_IDS.clinic.yearly]: 365,
  [PRODUCT_IDS.clinic.blueTick]: 30,
  [PRODUCT_IDS.clinic.reels]: 365,
  [PRODUCT_IDS.clinic.featured]: 30,
  [PRODUCT_IDS.hospital.yearly]: 365,
  [PRODUCT_IDS.hospital.blueTick]: 30,
  [PRODUCT_IDS.hospital.reels]: 30,
  [PRODUCT_IDS.hospital.featured]: 30,
};

const PATIENT_PLAN_LIMITS = {
  [PRODUCT_IDS.patient.basic]: { voice: 1, video: 1, chat: 1 },
  [PRODUCT_IDS.patient.premium]: { voice: 2, video: 2, chat: 2 },
  [PRODUCT_IDS.patient.gold]: { voice: 3, video: 3, chat: 3 },
  [PRODUCT_IDS.patient.platinum]: { voice: 5, video: 5, chat: 5 },
};

// ─── Debug logging helpers ────────────────────────────────────────────────────
const IAP_LOG_PREFIX = '[Apple IAP]';
function iapLog(msg, data) {
  if (data !== undefined) {
    console.log(`${IAP_LOG_PREFIX} ${msg}`, data);
  } else {
    console.log(`${IAP_LOG_PREFIX} ${msg}`);
  }
}
function iapError(msg, data) {
  if (data !== undefined) {
    console.error(`${IAP_LOG_PREFIX} ❌ ${msg}`, data);
  } else {
    console.error(`${IAP_LOG_PREFIX} ❌ ${msg}`);
  }
}
function iapWarn(msg, data) {
  if (data !== undefined) {
    console.warn(`${IAP_LOG_PREFIX} ⚠️ ${msg}`, data);
  } else {
    console.warn(`${IAP_LOG_PREFIX} ⚠️ ${msg}`);
  }
}

// ─── Format a CDV-Purchase error object for display ──────────────────────────
// BUG FIX (BUG 7): Never hide the real StoreKit error. Always expose:
//   - native iOS error code
//   - native iOS error message
//   - react-native-iap / cdv-purchase error code
//   - purchase status
function formatIAPError(error) {
  if (!error) return 'Unknown IAP error';
  const parts = [];
  if (error.message) parts.push(error.message);
  if (error.code !== undefined) parts.push(`Code: ${error.code}`);
  if (error.platform) parts.push(`Platform: ${error.platform}`);
  if (error.productId) parts.push(`Product: ${error.productId}`);
  return parts.join(' | ') || 'IAP error (no details)';
}

// ─── Extract the best available receipt data from a CDV-Purchase receipt ─────
// BUG FIX (BUG 4): receipt.receiptData does not exist on VerifiedReceipt.
// The real base64 appStoreReceipt lives at:
//   receipt.sourceReceipt?.nativeData?.appStoreReceipt  (SK1)
//   receipt.nativeData?.appStoreReceipt                  (direct access)
//   receipt.receiptData                                   (legacy fallback)
function extractReceiptData(receipt) {
  return (
    receipt?.sourceReceipt?.nativeData?.appStoreReceipt ||
    receipt?.nativeData?.appStoreReceipt ||
    receipt?.receiptData ||
    ''
  );
}

class AppleIAPService {
  constructor() {
    this.initialized = false;
    this.initializing = false;
    this.initializingPromise = null;
    this.products = [];
    this.productMap = {};
    this.listeners = [];
    this.pendingTransactions = [];
  }

  isAvailable() {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  }

  async initialize() {
    if (this.initialized) {
      iapLog('Already initialized. Products available:', this.products.length);
      return this.products;
    }
    if (this.initializing && this.initializingPromise) {
      iapLog('Initialization already in progress — waiting...');
      return this.initializingPromise;
    }
    this.initializing = true;
    this.initializingPromise = this._doInitialize();

    try {
      return await this.initializingPromise;
    } finally {
      this.initializingPromise = null;
    }
  }

  async registerDynamicProducts(productIds) {
    if (!productIds || productIds.length === 0) return;
    
    const newIds = productIds.filter(id => !this.productMap[id]);
    if (newIds.length === 0) return;

    iapLog('Registering dynamic products:', newIds);
    
    const registrations = newIds.map(id => ({
      id,
      type: PRODUCT_TYPE_MAP[id] || ProductType.PAID_SUBSCRIPTION,
      platform: APPLE_PLATFORM,
    }));

    store.register(registrations);
    if (this.initialized) {
      await store.update();
      // Wait briefly for productUpdated events
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (store.products) {
        for (const p of store.products) {
          if (!this.productMap[p.id] && newIds.includes(p.id)) {
            const data = {
              id: p.id,
              title: p.title,
              description: p.description,
              price: p.price,
              localizedPrice: p.localizedPrice,
              currency: p.currency,
              group: p.group,
              type: p.type,
            };
            this.products.push(data);
            this.productMap[p.id] = data;
          }
        }
        this._notifyListeners('products-updated', this.products);
      }
    }
  }

  async _doInitialize() {
    try {
      iapLog('▶ Starting initialization...');

      // Enable verbose store logging to help diagnose App Review failures
      store.verbosity = LogLevel.DEBUG;

      const productIDs = allProductIDs();
      iapLog('Products to register:', productIDs);

      const registrations = productIDs.map(id => ({
        id,
        type: PRODUCT_TYPE_MAP[id] || ProductType.PAID_SUBSCRIPTION,
        platform: APPLE_PLATFORM,
      }));

      iapLog('Registrations (with types):', registrations.map(r => `${r.id} → ${r.type}`));
      store.register(registrations);

      // BUG FIX (BUG 3): REMOVED the stub validator that was bypassing real validation:
      //   store.validator = (request, callback) => { callback({ ok: true }); }
      // That stub was causing VerifiedReceipt to be synthetic (no real receipt data),
      // so the backend never received a valid receipt and never activated the subscription.
      //
      // The correct flow is:
      //   .approved() → transaction.verify() → [CDV Purchase calls store.validator if set]
      //                                      → .verified() fires → we send receipt to backend
      //
      // Since we do NOT set store.validator, CDV-Purchase passes the receipt directly to
      // the .verified() handler with the real native receipt data attached.

      store.when()
        .productUpdated(product => {
          iapLog('productUpdated:', {
            id: product.id,
            title: product.title,
            price: product.price,
            localizedPrice: product.localizedPrice,
            currency: product.currency,
            type: product.type,
            state: product.state,
          });
          const idx = this.products.findIndex(p => p.id === product.id);
          const data = {
            id: product.id,
            title: product.title,
            description: product.description,
            price: product.price,
            localizedPrice: product.localizedPrice,
            currency: product.currency,
            group: product.group,
            type: product.type,
          };
          if (idx >= 0) {
            this.products[idx] = data;
          } else {
            this.products.push(data);
          }
          this.productMap[product.id] = data;
          this._notifyListeners('products-updated', this.products);
        })
        .approved(async transaction => {
          iapLog('✅ Transaction approved — verifying...', {
            transactionId: transaction.transactionId,
            products: transaction.products,
            state: transaction.state,
          });
          try {
            await transaction.verify();
          } catch (err) {
            iapError('transaction.verify() failed', err);
          }
        })
        .verified(async receipt => {
          // BUG FIX: VerifiedReceipt does not have a .transactions property.
          // Extract from sourceReceipt, nativeTransactions, or collection.
          const transaction = receipt.sourceReceipt?.transactions?.[0] || receipt.nativeTransactions?.[0];
          const productId = transaction?.products?.[0]?.id || transaction?.productId || receipt.collection?.[0]?.id;

          iapLog('✅ Receipt verified — sending to backend', {
            productId,
            transactionId: transaction?.transactionId,
            originalTransactionId: transaction?.originalTransactionId,
            purchaseDate: transaction?.purchaseDate,
            receiptDataLength: extractReceiptData(receipt)?.length || 0,
            platform: receipt.platform,
          });

          if (!transaction) {
            iapError('verified receipt has no transactions', receipt);
            this._notifyListeners('purchase-failed', { error: 'No transaction in verified receipt' });
            return;
          }

          try {
            const result = await this._sendReceiptToBackend(receipt, transaction, productId);
            if (result.success) {
              iapLog('✅ Backend receipt verification succeeded', result.data);
              await receipt.finish();
              iapLog('✅ Transaction finished (finishTransaction called)');
              this._notifyListeners('purchase-completed', {
                productId,
                transactionId: transaction.transactionId,
                data: result.data,
              });
            } else {
              iapError('Backend receipt verification failed', result.error);
              this._notifyListeners('purchase-failed', {
                error: result.error || 'Receipt verification failed on server',
              });
            }
          } catch (err) {
            iapError('_sendReceiptToBackend threw an exception', err);
            this._notifyListeners('purchase-failed', { error: err.message });
          }
        })
        .unverified(receipt => {
          iapError('Unverified receipt received', {
            platform: receipt.platform,
            transactions: receipt.transactions?.length,
          });
          this._notifyListeners('purchase-failed', { error: 'Receipt verification failed (unverified)' });
        });

      // BUG FIX (BUG 7): Expose the full StoreKit error — never hide it.
      store.error(error => {
        const fullMessage = formatIAPError(error);
        iapError('StoreKit error received', {
          code: error.code,
          message: error.message,
          platform: error.platform,
          productId: error.productId,
          source: error.source,
        });

        if (error.code === ErrorCode.PAYMENT_CANCELLED) {
          iapLog('User cancelled the purchase');
          this._notifyListeners('purchase-cancelled', { error: 'User cancelled' });
          // Also notify purchase-failed so purchase() promise cleans up
          this._notifyListeners('purchase-failed', { error: 'User cancelled', cancelled: true });
        } else {
          this._notifyListeners('purchase-failed', {
            error: fullMessage,
            code: error.code,
            nativeMessage: error.message,
            productId: error.productId,
          });
        }
      });

      store.ready(() => {
        iapLog('✅ store.ready() callback fired. Store is fully initialized.');
      });

      iapLog('Calling store.initialize([APPLE_APPSTORE])...');
      await Promise.race([
        store.initialize([APPLE_PLATFORM]),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Initialization timed out after 15 seconds')), 15000)
        ),
      ]);
      iapLog('store.initialize() returned');

      iapLog('Calling store.update() to refresh product prices...');
      await store.update();
      iapLog('store.update() returned');

      // Wait for productUpdated events to fire after initialization
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (store.products && store.products.length > 0) {
        this.products = store.products.map(p => ({
          id: p.id,
          title: p.title,
          description: p.description,
          price: p.price,
          localizedPrice: p.localizedPrice,
          currency: p.currency,
          group: p.group,
          type: p.type,
        }));
        for (const p of this.products) {
          this.productMap[p.id] = p;
        }
      }

      iapLog('✅ Initialization complete.', {
        productsLoaded: this.products.length,
        products: this.products.map(p => ({
          id: p.id,
          price: p.localizedPrice || p.price,
          type: p.type,
        })),
      });

      // Warn if any of the 4 required patient product IDs are missing
      const requiredPatientIds = Object.values(PRODUCT_IDS.patient);
      const missingIds = requiredPatientIds.filter(id => !this.productMap[id]);
      if (missingIds.length > 0) {
        iapError(
          '⚠️ The following product IDs were NOT returned by App Store Connect. ' +
          'Verify they exist in App Store Connect and are in READY FOR SALE status.',
          missingIds
        );
      }

      this.initialized = true;
      this.initializing = false;
      this._notifyListeners('initialized', this.products);
      return this.products;
    } catch (error) {
      this.initializing = false;
      this.initialized = false;
      iapError('Initialization failed', {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      this._notifyListeners('init-failed', { error: error.message || 'Initialization failed' });
      throw error;
    }
  }

  on(event, callback) {
    this.listeners.push({ event, callback });
    return () => {
      this.listeners = this.listeners.filter(l => l.callback !== callback);
    };
  }

  _notifyListeners(event, data) {
    for (const { event: e, callback } of this.listeners) {
      if (e === event) {
        try { callback(data); } catch (err) { iapError('Listener error for event: ' + e, err); }
      }
    }
  }

  getProduct(productId) {
    return this.productMap[productId] || null;
  }

  getProducts() {
    return [...this.products];
  }

  async purchase(productId) {
    if (!this.initialized) {
      iapLog('Not initialized — calling initialize() before purchase...');
      await this.initialize();
    }

    iapLog('▶ purchase() called for product:', productId);

    // Verify product exists before attempting purchase
    const product = this.productMap[productId];
    if (!product) {
      iapError('Product not found in productMap — it may not have loaded from App Store Connect', productId);
      iapError('Available products:', Object.keys(this.productMap));
    }

    return new Promise((resolve, reject) => {
      let settled = false;
      let removeCompletedListener;
      let removeFailedListener;
      let removeCancelledListener;

      function cleanup() {
        if (removeCompletedListener) removeCompletedListener();
        if (removeFailedListener) removeFailedListener();
        if (removeCancelledListener) removeCancelledListener();
        clearTimeout(timeout);
      }

      // BUG FIX (BUG 9): Guard against double-settlement from race conditions
      function settleOnce(fn) {
        if (!settled) {
          settled = true;
          cleanup();
          fn();
        }
      }

      removeCompletedListener = this.on('purchase-completed', (result) => {
        if (result.productId === productId) {
          iapLog('purchase-completed event received for productId:', productId);
          settleOnce(() => resolve(result));
        }
      });

      removeFailedListener = this.on('purchase-failed', (result) => {
        iapLog('purchase-failed event received:', result);
        settleOnce(() => {
          if (result.cancelled) {
            reject(new Error('User cancelled'));
          } else {
            // BUG FIX (BUG 7): Preserve the full error detail for the caller
            const err = new Error(result.error || 'Purchase failed');
            err.iapCode = result.code;
            err.nativeMessage = result.nativeMessage;
            err.productId = result.productId;
            reject(err);
          }
        });
      });

      removeCancelledListener = this.on('purchase-cancelled', () => {
        settleOnce(() => reject(new Error('User cancelled')));
      });

      const timeout = setTimeout(() => {
        iapError('Purchase timed out after 60 seconds for product:', productId);
        settleOnce(() => reject(new Error('Purchase timed out')));
      }, 60000);

      const cdvProduct = store.get(productId);
      if (!cdvProduct) {
        iapError('Product not found in store cache for order:', productId);
        return settleOnce(() => reject(new Error('Product not loaded from App Store')));
      }

      const offer = cdvProduct.getOffer();
      if (!offer) {
        iapError('No offer found for product:', productId);
        return settleOnce(() => reject(new Error('No purchase offer available for this product')));
      }

      iapLog('Calling store.order() for offer:', offer.id);
      store.order(offer).catch(err => {
        iapError('store.order() rejected immediately', {
          message: err.message,
          code: err.code,
          productId,
        });
        settleOnce(() => {
          if (err.code === ErrorCode.PAYMENT_CANCELLED) {
            reject(new Error('User cancelled'));
          } else {
            const wrappedErr = new Error(formatIAPError(err));
            wrappedErr.iapCode = err.code;
            reject(wrappedErr);
          }
        });
      });
    });
  }

  async restorePurchases() {
    if (!this.initialized) {
      iapLog('Not initialized — calling initialize() before restorePurchases...');
      await this.initialize();
    }

    iapLog('▶ restorePurchases() called');

    return new Promise((resolve, reject) => {
      let settled = false;
      let removeListener;

      function settleOnce(fn) {
        if (!settled) {
          settled = true;
          if (removeListener) removeListener();
          fn();
        }
      }

      removeListener = this.on('purchase-completed', (result) => {
        iapLog('purchase-completed during restore:', result);
        settleOnce(() => resolve(result));
      });

      store.restorePurchases()
        .then(() => {
          iapLog('✅ restorePurchases() complete');
          settleOnce(() => resolve({ success: true }));
        })
        .catch(err => {
          iapError('restorePurchases() failed', err);
          settleOnce(() => reject(err));
        });
    });
  }

  async _sendReceiptToBackend(receipt, transaction, productId) {
    try {
      if (!productId) {
        iapWarn('productId is undefined in _sendReceiptToBackend. Backend verification may fail.');
      }

      // BUG FIX (BUG 4): receipt.receiptData does not exist on VerifiedReceipt.
      // Extract the real appStoreReceipt base64 from the native data.
      const receiptData = extractReceiptData(receipt);

      iapLog('Sending receipt to backend', {
        productId,
        transactionId: transaction.transactionId,
        originalTransactionId: transaction.originalTransactionId,
        purchaseDate: transaction.purchaseDate,
        receiptDataLength: receiptData.length,
        receiptDataEmpty: receiptData.length === 0,
      });

      if (!receiptData) {
        iapWarn(
          'receiptData is empty — backend validation may fail. ' +
          'This can happen when CDV-Purchase is running in SK2 mode (no monolithic receipt). ' +
          'The transaction ID will still be used for deduplication.'
        );
      }

      const extraData = {};
      if (SUBSCRIPTION_DURATION_MAP[productId]) {
        extraData.durationDays = SUBSCRIPTION_DURATION_MAP[productId];
      }
      if (PATIENT_PLAN_LIMITS[productId]) {
        extraData.limits = PATIENT_PLAN_LIMITS[productId];
      }

      const res = await authAPI.verifyAppleReceipt({
        productId,
        transactionId: transaction.transactionId,
        receiptData,
        originalTransactionId: transaction.originalTransactionId,
        purchaseDate: transaction.purchaseDate,
        extraData,
      });

      iapLog('Backend response:', res.data);
      return { success: true, data: res.data };
    } catch (error) {
      iapError('_sendReceiptToBackend failed', {
        message: error.message,
        responseData: error.response?.data,
        status: error.response?.status,
      });
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  async getSubscriptionStatus(productId) {
    if (!this.initialized) {
      await this.initialize();
    }
    try {
      iapLog('getSubscriptionStatus() for product:', productId);
      const res = await store.getSubscriptionStatus(productId);
      iapLog('getSubscriptionStatus() result:', res);
      return res;
    } catch (err) {
      iapError('getSubscriptionStatus() failed', err);
      return null;
    }
  }
}

const appleIAP = new AppleIAPService();
export default appleIAP;
export { PRODUCT_IDS, PATIENT_PLAN_LIMITS, SUBSCRIPTION_DURATION_MAP };
